// React and hooks
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, Outlet, useLocation } from 'react-router-dom';

// Assets
import logo from '../assets/Logo.png';
import background from '../assets/SGI.avif';

// Animation
import { motion, AnimatePresence } from 'framer-motion';

// Icons (Lucide)
import {
  Sun,
  Moon,
  Package,
  ClipboardList,
  ShoppingCart,
  BarChart2,
  LogOut,
  Settings,
  Globe, 
  Tag,
  Truck,
  DollarSign,
  Cpu,
  User,
  ShieldCheck
} from 'lucide-react';

// Icons (React-Icons)
import { FaSnowflake, FaWarehouse } from 'react-icons/fa';
import { GiMeat } from 'react-icons/gi';

// Theme hook
import useTheme from '../hooks/useTheme';

// i18n
import { useTranslation } from 'react-i18next';

const HomeMenu = () => {
  const authRaw = localStorage.getItem('auth') || '{}';
  const auth = JSON.parse(authRaw);
  const isAdmin = String(auth?.role || '').toUpperCase() === 'ADMIN';

  const navigate = useNavigate();
  const location = useLocation();
  const mainContentRef = useRef(null);
  const { theme, toggleTheme } = useTheme();
  const { t, i18n } = useTranslation();

  const isIOS =
    typeof navigator !== 'undefined' &&
    /iPad|iPhone|iPod/.test(navigator.userAgent);

  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== 'undefined' && window.matchMedia('(max-width: 1023px)').matches
  );

  const [collapsed, setCollapsed] = useState(() => {
    return localStorage.getItem('sidebarCollapsed') === 'false' ? false : true;
  });

  const isInventoryRoute = location.pathname.startsWith('/products/inventory/');
  const isManageRoute = [
    '/product-templates',
    '/categories',
    '/brands',
    '/suppliers',
    '/clients',
    '/admin/users',
  ].some((path) => location.pathname.startsWith(path));

  const [manageOpen, setManageOpen] = useState(isManageRoute);
  const [inventoryOpen, setInventoryOpen] = useState(isInventoryRoute);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const showNavLabels = !collapsed || isMobile;

  // Save collapsed state in localStorage
  useEffect(() => {
    localStorage.setItem('sidebarCollapsed', collapsed);
  }, [collapsed]);

  // Keep the active navigation group open when moving between its pages
  useEffect(() => {
    if (isInventoryRoute) setInventoryOpen(true);
    if (isManageRoute) setManageOpen(true);
  }, [isInventoryRoute, isManageRoute]);

  // Start each destination at the top of the content area without animated scrolling
  useEffect(() => {
    if (mainContentRef.current) mainContentRef.current.scrollTop = 0;
  }, [location.pathname]);

  // Redirect to login if no token
  useEffect(() => {
    if (!auth?.token) {
      navigate('/login', { replace: true });
    }
  }, [auth?.token, navigate]);

  // Handle logout
  const handleLogout = () => {
    localStorage.removeItem('auth');
    navigate('/login');
  };

  // Toggle language EN <-> ES
  const toggleLanguage = () => {
    const newLang = i18n.language === 'en' ? 'es' : 'en';
    i18n.changeLanguage(newLang);
  };

  // Navigate
  const handleNavigate = (path) => {
    if (isMobile) setMobileMenuOpen(false);
    navigate(path);
  };

  // Reusable nav button
  const navBtn = (onClick, icon, title, label = '', isActive = false) => (
    <button
      onClick={onClick}
      title={title}
      aria-current={isActive ? 'page' : undefined}
      className={`flex w-full items-center gap-2 rounded-md px-3 py-2.5 transition-colors
        ${isActive
          ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-200'
          : 'text-gray-800 hover:bg-gray-200 dark:text-gray-200 dark:hover:bg-gray-700'}`}
    >
      <div className="flex w-6 shrink-0 justify-center">{icon}</div>
      <AnimatePresence initial={false}>
        {showNavLabels && (
          <motion.span
            key={`nav-label-${title}`}
            initial={{ opacity: 0, width: 0 }}
            animate={{ opacity: 1, width: 'auto' }}
            exit={{ opacity: 0, width: 0 }}
            transition={{ duration: 0.2, ease: 'easeInOut' }}
            className="overflow-hidden whitespace-nowrap text-sm"
          >
            {label}
          </motion.span>
        )}
      </AnimatePresence>
    </button>
  );

  // Compact, indented navigation link used by expandable menu groups
  const subNavBtn = (path, icon, title, label) => {
    const isActive = location.pathname === path;

    return (
      <button
        onClick={() => handleNavigate(path)}
        title={title}
        aria-current={isActive ? 'page' : undefined}
        className={`flex w-full items-center gap-2 rounded-md py-1.5 pl-2 pr-2 text-left transition-colors
          ${isActive
            ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-200'
            : 'text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700/70'}`}
      >
        <span className="flex w-5 shrink-0 justify-center">{icon}</span>
        <span className="truncate text-[13px]">{label}</span>
      </button>
    );
  };

  const sidebarContent = (
    <div className="flex h-full min-h-0 flex-col p-3">
      {/* Header with logo */}
      <div className={`mb-2 flex items-center ${collapsed && !isMobile ? 'justify-center' : ''}`}>
        <motion.button
          type="button"
          onClick={() => {
            if (isMobile) {
              setMobileMenuOpen(false);
            } else {
              setCollapsed((value) => !value);
            }
          }}
          aria-label={isMobile ? 'Cerrar menú' : collapsed ? 'Expandir menú' : 'Contraer menú'}
          aria-expanded={isMobile ? mobileMenuOpen : !collapsed}
          className="flex h-10 min-w-10 items-center justify-center gap-2 overflow-hidden rounded-md
                     hover:bg-gray-200 dark:hover:bg-gray-700"
        >
          <img src={logo} alt="Logo de Cárnicos De México" className="h-8 w-8 shrink-0" />
          <AnimatePresence initial={false}>
            {showNavLabels && (
              <motion.span
                key="company-name"
                initial={{ opacity: 0, width: 0 }}
                animate={{ opacity: 1, width: 'auto' }}
                exit={{ opacity: 0, width: 0 }}
                transition={{ duration: 0.22, ease: 'easeInOut' }}
                className="whitespace-nowrap text-sm font-bold text-gray-900 dark:text-white"
              >
                Cárnicos De México
              </motion.span>
            )}
          </AnimatePresence>
        </motion.button>
      </div>

      <nav className="min-h-0 flex-1 space-y-1">
      {/* Main navigation */}
      {navBtn(() => handleNavigate('/yields'), <BarChart2 size={20} />, t('yields'), t('yields'), location.pathname === '/yields')}
      {navBtn(() => handleNavigate('/orders'), <ClipboardList size={20} />, t('orders'), t('orders'), location.pathname === '/orders')}
      {navBtn(() => handleNavigate('/prices'), <DollarSign size={20} />, t('prices'), t('prices'), location.pathname === '/prices')}
      {navBtn(() => handleNavigate('/sales'), <ShoppingCart size={20} />, t('sales'), t('sales'), location.pathname === '/sales')}

      {/* Inventory with submenu */}
      <div>
        {navBtn(
          () => setInventoryOpen(!inventoryOpen),
          <Package size={20} />,
          t('inventory'),
          t('inventory'),
          isInventoryRoute
        )}

        <AnimatePresence>
          {inventoryOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.16, ease: 'easeOut' }}
              className={`mt-0.5 space-y-0.5 border-l border-gray-300 py-0.5 pl-1.5 dark:border-gray-600 ${collapsed && !isMobile ? 'ml-1' : 'ml-7'}`}
            >
              {subNavBtn('/products/inventory/coldstorage1', <FaSnowflake size={15} />, t('coldStorage1'), t('coldStorage1'))}
              {subNavBtn('/products/inventory/coldstorage2', <FaSnowflake size={15} />, t('coldStorage2'), t('coldStorage2'))}
              {subNavBtn('/products/inventory/coldstorage3', <FaSnowflake size={15} />, t('coldStorage3'), t('coldStorage3'))}
              {subNavBtn('/products/inventory/refrigerators', <GiMeat size={15} />, t('refrigerators'), t('refrigerators'))}
              {subNavBtn('/products/inventory/totems', <Cpu size={15} />, t('totems'), t('totems'))}
              {subNavBtn('/products/inventory/warehouse', <FaWarehouse size={15} />, t('warehouse'), t('warehouse'))}
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Manage with submenu */}
      <div>
        {navBtn(
          () => setManageOpen(!manageOpen),
          <Settings size={20} />,
          t('manage'),
          t('manage'),
          isManageRoute
        )}

        <AnimatePresence>
          {manageOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.16, ease: 'easeOut' }}
              className={`mt-0.5 space-y-0.5 border-l border-gray-300 py-0.5 pl-1.5 dark:border-gray-600 ${collapsed && !isMobile ? 'ml-1' : 'ml-7'}`}
            >
              {subNavBtn('/product-templates', <Package size={15} />, t('products'), t('products'))}
              {subNavBtn('/categories', <ClipboardList size={15} />, t('categories'), t('categories'))}
              {subNavBtn('/brands', <Tag size={15} />, t('brands'), t('brands'))}
              {subNavBtn('/suppliers', <Truck size={15} />, t('suppliers'), t('suppliers'))}
              {subNavBtn('/clients', <User size={15} />, t('clients'), t('clients'))} {/* 👈 Clients */}
              {isAdmin && subNavBtn('/admin/users', <ShieldCheck size={15} />, t('adminUsers'), t('adminUsers'))}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
      </nav>

      {/* Footer with language/theme/logout */}
      {navBtn(toggleLanguage, <Globe size={18} />, i18n.language.toUpperCase(), i18n.language.toUpperCase())}
      {navBtn(toggleTheme, theme === 'dark' ? <Sun size={18} /> : <Moon size={18} />, t('toggleTheme'), t('toggleTheme'))}
      {navBtn(handleLogout, <LogOut size={18} />, t('logOut'), t('logOut'))}
    </div>
  );

  // Dynamic viewport height fix for Safari (handles keyboard open/close)
  useEffect(() => {
    const updateViewport = () => setIsMobile(window.matchMedia('(max-width: 1023px)').matches);
    window.addEventListener('resize', updateViewport);
    updateViewport();
    return () => window.removeEventListener('resize', updateViewport);
  }, []);

  useEffect(() => {
    const setAppHeight = () => {
      const doc = document.documentElement;
      doc.style.setProperty('--app-height', `${window.visualViewport?.height || window.innerHeight}px`);
    };
    window.addEventListener('resize', setAppHeight);
    setAppHeight();
    return () => window.removeEventListener('resize', setAppHeight);
  }, []);

  // Compact layout is applied to phone sized screens.
  useEffect(() => {
    if (window.innerWidth < 500) {
      document.body.classList.add('iphone-compact');
    } else {
      document.body.classList.remove('iphone-compact');
    }
  }, [isMobile]);

  return (
    <div
      className="flex overflow-hidden dark:bg-gray-900"
      style={{
        height: 'var(--app-height)', // 👈 Dynamic viewport height variable
        backgroundImage: `url(${background})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        backgroundAttachment: isIOS ? 'scroll' : 'fixed', // 👈 Fix for iOS Safari keyboard issue
      }}
    >
      <div className="absolute inset-0 bg-white/80 dark:bg-black/70 z-0" />

      <AnimatePresence>
        {isMobile && mobileMenuOpen && (
          <motion.button
            type="button"
            aria-label={t('closeMenu')}
            className="fixed inset-0 z-20 bg-black/40"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setMobileMenuOpen(false)}
          />
        )}
      </AnimatePresence>

      {/* Sidebar: drawer on mobile, collapsible sidebar on larger screens */}
      <motion.aside
        initial={false}
        animate={isMobile
          ? { x: mobileMenuOpen ? 0 : '-100%', width: 280 }
          : { x: 0, width: collapsed ? 80 : 240 }}
        transition={{ duration: 0.3, ease: 'easeInOut' }}
        className={`inset-y-0 left-0 flex flex-col h-full bg-white/95 dark:bg-gray-800/95 backdrop-blur-md shadow-lg ${
          isMobile ? 'fixed z-30' : 'relative z-10'
        }`}
      >
        {sidebarContent}
      </motion.aside>

      {isMobile && !mobileMenuOpen && (
        <button
          type="button"
          onClick={() => setMobileMenuOpen(true)}
          aria-label={t('openMenu')}
          aria-expanded={false}
          className="fixed left-3 top-3 z-20 flex h-11 w-11 items-center justify-center rounded-lg
                     bg-white/90 text-gray-900 shadow-md backdrop-blur-md dark:bg-gray-800/90 dark:text-white"
        >
          <img src={logo} alt={`${t('openMenu')} ${t('companyName')}`} className="h-8 w-8" />
        </button>
      )}

      {/* Main content */}
      <main
        ref={mainContentRef}
        className={`flex-1 min-w-0 relative z-10 p-3 pt-16 sm:p-6 text-gray-900 dark:text-white overflow-auto ${
          isMobile ? 'transition-all duration-300' : ''
        }`}
      >
        <Outlet />
      </main>
    </div>
  );
};

export default HomeMenu;
